import React, { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar/Navbar';
import Footer from '../components/Footer/Footer';
import { getProducts } from '../utils/productStore';
import { brochures } from '../data/brochures';
import { findProductByRoute, productPath, PRODUCTS_HASH } from '../utils/routes';
import { HOME_SEO, productSchema, useSeo } from '../utils/seo';
import './ProductDetailPage.scss';

const ProductDetailPage = () => {
  const { category, productName } = useParams();
  const products = getProducts();
  const product = findProductByRoute(products, { category, productName });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [category, productName]);

  const pageTitle = product
    ? `${product.name} ${product.category} | Aavjo Industries`
    : HOME_SEO.title;
  const pageDescription = product
    ? product.paragraphs?.[0] ||
      `Buy ${product.name} ${product.category} from Aavjo Industries, aerial lift and material handling equipment manufacturer in Ahmedabad, Gujarat.`
    : HOME_SEO.description;

  useSeo({
    title: pageTitle,
    description: pageDescription,
    keywords: product
      ? `${product.name}, ${product.category}, ${product.category.toLowerCase()} manufacturer, aerial lift, Aavjo Industries, Ahmedabad`
      : HOME_SEO.keywords,
    path: product ? productPath(product) : '/',
    jsonLd: product ? productSchema(product) : undefined,
  });

  if (!product) {
    return <Navigate to={PRODUCTS_HASH} replace />;
  }

  const decodedName = decodeURIComponent(productName || '').replace(/\+/g, ' ').trim();
  if (decodedName.toLowerCase() !== product.name.toLowerCase()) {
    return <Navigate to={productPath(product)} replace />;
  }

  const enquiryUrl = `/?product=${encodeURIComponent(product.name)}#contact`;
  const related = products
    .filter((p) => p.category === product.category && p.name !== product.name)
    .slice(0, 4);

  return (
    <div className="product-detail-page">
      <Navbar />

      <div className="container product-detail-container">
        <Link to={PRODUCTS_HASH} className="product-detail-back">
          <span className="back-arrow" aria-hidden="true">&#8592;</span>
          Back to Products
        </Link>

        <div className="row g-4 g-lg-5 product-detail-row">
          <div className="col-lg-6">
            <div className="product-detail-gallery">
              <span className="gallery-tab">Pictures</span>
              <div className="gallery-main">
                <img
                  src={product.image}
                  alt={`${product.name} ${product.category} by Aavjo Industries`}
                />
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <span className="gallery-tab gallery-tab-spacer" aria-hidden="true">
              Pictures
            </span>
            <div className="product-detail-info">
              <h1 className="product-detail-title">{product.category}</h1>
              <p className="product-detail-model">{product.name}</p>

              {product.paragraphs.map((para, i) => (
                <p key={i} className="product-detail-desc">
                  {para}
                </p>
              ))}

              <ul className="product-detail-features">
                {product.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>

              <div className="product-detail-actions">
                <div className="brochure-buttons">
                  {brochures.map((b) => (
                    <a
                      key={b.id}
                      href={b.file}
                      download={b.downloadName}
                      className="btn-detail btn-detail-outline"
                    >
                      Download {b.label}
                    </a>
                  ))}
                </div>
                <Link to={enquiryUrl} className="btn-detail btn-detail-primary">
                  Enquire About This Product
                </Link>
              </div>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <section className="related-products">
            <h2>More {product.category} models from Aavjo Industries</h2>
            <ul>
              {related.map((item) => (
                <li key={item.name}>
                  <Link to={productPath(item)}>
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
